"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { LogLevel } from "@/hooks/telemetry";

const CPREPAGE_MIN_VISIBLE_MS = 1400;
const LANDING_PERF_PREPAGE_VISIBLE_MESSAGE = "prepage-visible";
const LANDING_PERF_PREPAGE_UNMOUNTED_MESSAGE = "prepage-unmounted";

const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

export interface LandingPrepageState {
  prepageMinElapsed: boolean;
  shouldRenderPrepage: boolean;
  showPrepage: boolean;
}

type LandingPerfPrepageLog = (
  message: string,
  payload: Record<string, unknown>,
  level: LogLevel,
) => void;

interface UseLandingPrepageInput {
  landingPerfPrepageLog: LandingPerfPrepageLog;
  viewportLoaded: boolean;
}

export function useLandingPrepage({
  landingPerfPrepageLog,
  viewportLoaded,
}: UseLandingPrepageInput): LandingPrepageState {
  const [shouldRenderPrepage, setShouldRenderPrepage] = useState(false);
  const [prepageMinElapsed, setPrepageMinElapsed] = useState(false);

  useIsomorphicLayoutEffect(() => {
    setShouldRenderPrepage(true);
    const timer = window.setTimeout(
      () => setPrepageMinElapsed(true),
      CPREPAGE_MIN_VISIBLE_MS,
    );
    return () => window.clearTimeout(timer);
  }, []);

  const showPrepage =
    shouldRenderPrepage && (!viewportLoaded || !prepageMinElapsed);
  const prepageVisibleStartRef = useRef<number | null>(null);

  useEffect(() => {
    if (showPrepage) {
      prepageVisibleStartRef.current = performance.now();
      landingPerfPrepageLog(
        LANDING_PERF_PREPAGE_VISIBLE_MESSAGE,
        {
          visibleStartMs: prepageVisibleStartRef.current,
          navigationStartMs: performance.timeOrigin,
        },
        LogLevel.Info,
      );
      return;
    }

    if (prepageVisibleStartRef.current == null) {
      return;
    }

    const unmountedMs = performance.now();
    landingPerfPrepageLog(
      LANDING_PERF_PREPAGE_UNMOUNTED_MESSAGE,
      {
        visibleStartMs: prepageVisibleStartRef.current,
        unmountedMs,
        durationMs: unmountedMs - prepageVisibleStartRef.current,
        navigationStartMs: performance.timeOrigin,
      },
      LogLevel.Info,
    );
    prepageVisibleStartRef.current = null;
  }, [landingPerfPrepageLog, showPrepage]);

  return {
    prepageMinElapsed,
    shouldRenderPrepage,
    showPrepage,
  };
}
